'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  SportsEventsClient,
  // Aliased so the DOM `Event` type stays available for addEventListener
  // handlers below (the sort-change listener uses CustomEvent<Event>).
  type Event as SportsEvent,
  type PaginatedEvents,
  type SearchEventsParams,
} from '@bip/api-client';
import { EventCard } from './EventCard';

interface EventsListProps {
  initialData: PaginatedEvents;
  apiBaseUrl: string;
  baseFilters?: Partial<SearchEventsParams>;
  entityName?: string;
}

export function EventsList({ initialData, apiBaseUrl, baseFilters, entityName }: EventsListProps) {
  const eventsClient = useMemo(() => new SportsEventsClient(apiBaseUrl), [apiBaseUrl]);
  const [events, setEvents] = useState<SportsEvent[]>(() => initialData.data);
  const [page, setPage] = useState(initialData.page);
  const [hasMore, setHasMore] = useState(initialData.page < initialData.totalPages);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(initialData.total);
  // Track the current `?search=` URL param so the all-events results header
  // can render `All events - <total> sports found for "<keyword>"`. Updated
  // alongside every refetch (filtersChanged + sort change handlers below).
  const [searchKeyword, setSearchKeyword] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('search')?.trim() ?? '';
  });
  // Local sort state — not read from the URL (sort is session-only and resets
  // on refresh). Fed by `sportsSortChanged` events from EventsFilters.
  const [sort, setSort] = useState<{ sortBy: string; sortOrder: string }>({
    sortBy: '',
    sortOrder: '',
  });
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEvents(initialData.data);
    setPage(initialData.page);
    setHasMore(initialData.page < initialData.totalPages);
    setTotal(initialData.total);
  }, [initialData]);

  // Broadcast current total to sibling components (EventsFilters listens so it
  // can disable all filter buttons when there's nothing to filter).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('sportsTotalChanged', { detail: { total } }));
  }, [total]);

  // Convert dateRange key to actual dateFrom/dateTo values
  const resolveDateRange = (
    dateRange: string | null,
    dateFrom: string | null,
    dateTo: string | null,
  ) => {
    if (!dateRange) return { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined };
    const fmt = (d: Date) => d.toLocaleDateString('en-GB'); // DD/MM/YYYY
    const now = new Date();
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    switch (dateRange) {
      case 'today': {
        const t = fmt(now);
        return { dateFrom: t, dateTo: t };
      }
      case 'next7days':
      case 'thisWeek': {
        const to = new Date(now);
        to.setDate(now.getDate() + 7);
        return { dateFrom: fmt(now), dateTo: fmt(to) };
      }
      case 'thisMonth':
        return { dateFrom: fmt(now), dateTo: fmt(endOfMonth) };
      default:
        return { dateFrom: dateFrom || undefined, dateTo: dateTo || undefined };
    }
  };

  // Build search query from current browser URL (+ local sort state).
  // Sort is intentionally NOT read from the URL — it's session-only state
  // owned in this component and defaults to dateOfEvent/asc on every load.
  const buildQueryFromUrl = useCallback((): SearchEventsParams => {
    const sp = new URLSearchParams(window.location.search);
    const { dateFrom, dateTo } = resolveDateRange(
      sp.get('dateRange'),
      sp.get('dateFrom'),
      sp.get('dateTo'),
    );
    return {
      search: sp.get('search') || undefined,
      cityId: sp.get('cityId') ? parseInt(sp.get('cityId')!) : undefined,
      // Multi-select filters: keep as CSV strings — parseInt would silently
      // collapse "1,2,3" to 1 and drop the rest of the selection.
      participantId: sp.get('participantId') || undefined,
      teamType: sp.get('teamType') || undefined,
      // Multi-select CSV — matches participantId. The concerts
      // backend accepts CSV via `parseIdList`. The shared
      // `SearchEventsParams.venueId` type is `number` for sports
      // parity; concerts widens it locally to string CSV via
      // `as any`.
      venueId: (sp.get('venueId') || undefined) as any,
      dateFrom,
      dateTo,
      minPrice: sp.get('minPrice') ? parseFloat(sp.get('minPrice')!) : undefined,
      maxPrice: sp.get('maxPrice') ? parseFloat(sp.get('maxPrice')!) : undefined,
      sortBy: sort.sortBy || 'dateOfEvent',
      sortOrder: (sort.sortOrder as 'asc' | 'desc') || 'asc',
      page: 1,
      limit: 12,
      ...(baseFilters || {}),
    };
  }, [baseFilters, sort]);

  // Re-fetch when EventsFilters changes URL via window.history.replaceState
  useEffect(() => {
    const handler = async () => {
      setIsLoading(true);
      try {
        const query = buildQueryFromUrl();
        const result = await eventsClient.searchEvents(query);
        setEvents(result.data);
        setPage(result.page);
        setHasMore(result.page < result.totalPages);
        setTotal(result.total);
        // Mirror `?search=` URL into local state so the H4 header reflects
        // the current keyword. Updated with the data, not on URL change
        // alone, so the heading and the listing never disagree.
        const sp = new URLSearchParams(window.location.search);
        setSearchKeyword(sp.get('search')?.trim() ?? '');
      } catch (err) {
        console.error('Error re-fetching events:', err);
      } finally {
        setIsLoading(false);
      }
    };
    window.addEventListener('filtersChanged', handler);
    return () => window.removeEventListener('filtersChanged', handler);
  }, [eventsClient, buildQueryFromUrl]);

  // Listen for sort changes from EventsFilters (sort is not in URL).
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ sortBy: string; sortOrder: string }>;
      setSort({
        sortBy: ce.detail?.sortBy || '',
        sortOrder: ce.detail?.sortOrder || '',
      });
    };
    window.addEventListener('sportsSortChanged', handler);
    return () => window.removeEventListener('sportsSortChanged', handler);
  }, []);

  // Refetch whenever sort changes. Skip the very first render — the server
  // already delivered the initial data with the default sort.
  const sortInitialized = useRef(false);
  useEffect(() => {
    if (!sortInitialized.current) {
      sortInitialized.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const query = buildQueryFromUrl();
        const result = await eventsClient.searchEvents(query);
        if (cancelled) return;
        setEvents(result.data);
        setPage(result.page);
        setHasMore(result.page < result.totalPages);
        setTotal(result.total);
      } catch (err) {
        if (!cancelled) console.error('Error re-fetching events on sort change:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sort, eventsClient, buildQueryFromUrl]);

  const loadMoreEvents = useCallback(async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const nextPage = page + 1;

      const query = buildQueryFromUrl();
      query.page = nextPage;

      const result = await eventsClient.searchEvents(query);
      setEvents((prev) => [...prev, ...result.data]);
      setPage(result.page);
      setHasMore(result.page < result.totalPages);
    } catch (error) {
      console.error('Error loading more events:', error);
    } finally {
      setIsLoading(false);
    }
  }, [page, hasMore, isLoading, eventsClient, buildQueryFromUrl]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMoreEvents();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [loadMoreEvents, hasMore, isLoading]);

  // Skeleton card placeholder
  const SkeletonCard = () => (
    <div className="col-lg-4 pb-4">
      <div className="skeleton-card">
        <div className="skeleton-line skeleton-badge"></div>
        <div className="skeleton-line skeleton-title"></div>
        <div className="skeleton-line skeleton-text"></div>
        <div className="skeleton-line skeleton-text short"></div>
        <div className="skeleton-line skeleton-text"></div>
        <div className="skeleton-line skeleton-price"></div>
        <div className="skeleton-line skeleton-btn"></div>
      </div>
    </div>
  );

  // Show skeleton on initial load (no events yet and total is 0 from server)
  const showSkeleton = events.length === 0 && total === 0 && hasMore;

  return (
    <section className="pb-5">
      <div className="container">
        {/* Results Header — "sports found" copy replaced with
            "concerts found" on the concerts domain. */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h4 className="mb-0">
            {total > 0 ? (
              entityName ? (
                <>
                  {entityName} - {total} concerts found
                </>
              ) : searchKeyword ? (
                // All-events listing page (no route-locked entity) with an
                // active `?search=` keyword — show the result heading per
                // design: `All events - 10 concerts found for "Madrid"`.
                <>
                  All events - {total} concerts found for &quot;{searchKeyword}&quot;
                </>
              ) : (
                <>{total} concerts found</>
              )
            ) : (
              ''
            )}
          </h4>
        </div>

        {/* Skeleton loader on initial load or filter change */}
        {(showSkeleton || isLoading) && (
          <div className="row">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Events Grid */}
        {!isLoading && events.length > 0 ? (
          <>
            <div className="row">
              {events.map((event, index) => (
                <EventCard key={`${event.id}-${index}`} event={event} />
              ))}
            </div>

            {/* Infinite scroll trigger + skeleton loader */}
            {hasMore && (
              <>
                <div ref={observerTarget} />
                {isLoading && (
                  <div className="row mt-3">
                    {[1, 2, 3].map((i) => (
                      <SkeletonCard key={`load-${i}`} />
                    ))}
                  </div>
                )}
                {!isLoading && <div className="events-loader-sentinel" />}
              </>
            )}

            {/* End of results */}
          </>
        ) : !isLoading ? (
          /* Empty state */
          <div className="text-center py-5">
            {/* <svg
              style={{ width: '6rem', height: '6rem', color: '#d1d5db' }}
              className="mx-auto mb-3 d-block"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg> */}
            <h3 className="">No Concerts Available</h3>
          </div>
        ) : null}
      </div>
    </section>
  );
}
